import { useCallback, useContext, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { ObjectPickerController } from '@components/hookFormControllers/ObjectPickerController'
import { ObjectPickerProps } from '@components/ui/ObjectPicker'

import { replaceVariablesInFilter, useOpenForm, useVariables } from '@hooks'
import { KIND_SEARCHABLE_FILTER_WORD } from '@constants'
import { GETVariableDTO } from '@types'

import { processFieldChangeVariable } from '../../../../hooks/processFieldChangeVariable'
import { FormContext } from '../../DisplayForm'
import { useExecuteCommandMutation } from '@redux/api'

type TProps = {
  generalFilterField?: string | null
  formId?: number
  isDialogWindow: boolean
  latestVariables?: GETVariableDTO[]
} & Omit<
  ObjectPickerProps,
  | 'value'
  | 'onChange'
  | 'setValue'
  | 'invalid'
  | 'onLoadObject'
  | 'onChangeInDropdownEntityMode'
  | 'dataValue'
  | 'generalFilterField'
>

export const ObjectPickerControllerVariableWrapper = ({
  formId,
  generalFilterField,
  latestVariables,
  ...props
}: TProps) => {
  const {openForm} = useOpenForm()
  const [executeCommand] = useExecuteCommandMutation()

  const globalVariables = useVariables({ isFetchAllVariables: true })
  const { form, instanceId } = useContext(FormContext)
  const { getValues } = useFormContext()

  const generalFilter = useMemo(() => {
    let updated = replaceVariablesInFilter(
      generalFilterField || '',
      KIND_SEARCHABLE_FILTER_WORD.FORM,
      latestVariables || []
    )
    updated = replaceVariablesInFilter(updated, 'global', globalVariables)

    return updated.includes(`'null'`) ||
      updated.includes('global.') ||
      updated.includes(`${KIND_SEARCHABLE_FILTER_WORD.FORM}.`)
      ? ''
      : updated
  }, [generalFilterField, latestVariables, globalVariables])

  // DDL может "тихо" обогатить option через onChange(..., false):
  // в этом случае RHF watch(type='change') не срабатывает, поэтому
  // вручную запускаем CHANGE_VARIABLE_VALUE для актуального значения поля.
  const handleSilentChange = useCallback(
    (fieldName: string) => {
      const formValues = getValues()

      processFieldChangeVariable(formValues, fieldName, {
        row: formValues,
        formVariables: latestVariables ?? [],
        outerVariables: undefined,
        actions: form?.actions ?? [],
        uniqueEventId: instanceId,
        openForm,
        executeCommand,
      })
    },
    [form?.actions, getValues, instanceId, latestVariables]
  )

  return (
    <ObjectPickerController
      formId={formId}
      generalFilterField={generalFilter}
      onSilentChange={handleSilentChange}
      {...props}
    />
  )
}
